import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../common/components/general/TSAuthProvider";
import { HOME_PAGE_ROUTE } from "../../../common/constants/routerConstants";
import config from "../../../config";

const MfaSettingsPage = () => {
  const { token } = useContext(AuthContext);
  const navigate  = useNavigate();

  // Guard — only email/password accounts can use MFA.
  // If a social user somehow navigates here, redirect home.
  const source = localStorage.getItem("source");
  useEffect(() => {
    if (source && source !== "basic") {
      navigate(HOME_PAGE_ROUTE);
    }
  }, [source, navigate]);

  const [status, setStatus]               = useState(null);
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");

  // TOTP setup
  const [setupData, setSetupData]         = useState(null);
  const [setupCode, setSetupCode]         = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Disable flow
  const [disableCode, setDisableCode]         = useState("");
  const [showDisable, setShowDisable]         = useState(false);
  const [disableSessionToken, setDisableSessionToken] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  // ── Fetch status ──────────────────────────────────────────────────────────
  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res  = await fetch(`${config.baseURL}api/user/mfa/status`, { headers: authHeader });
      const data = await res.json();
      setStatus(data);
    } catch {
      setError("Impossible de charger le statut MFA");
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => { setError(""); setSuccess(""); };

  // ── TOTP setup ─────────────────────────────────────────────────────────────
  const startTotpSetup = async () => {
    clearMessages(); setActionLoading(true);
    try {
      const res  = await fetch(`${config.baseURL}api/user/mfa/setup-totp`, { method: "POST", headers: authHeader });
      const data = await res.json();
      setSetupData(data);
    } catch {
      setError("Erreur lors de l'initialisation TOTP");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmTotpSetup = async () => {
    if (!setupCode.trim()) { setError("Veuillez saisir le code"); return; }
    clearMessages(); setActionLoading(true);
    try {
      const res = await fetch(`${config.baseURL}api/user/mfa/confirm-setup`, {
        method:  "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body:    JSON.stringify({ code: setupCode }),
      });
      if (res.ok) {
        const data = await res.json();
        setRecoveryCodes(data.recoveryCodes);
        setSetupData(null);
        setSetupCode("");
        setSuccess("TOTP activé avec succès !");
        fetchStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.message || "Code TOTP incorrect");
      }
    } catch {
      setError("Erreur de connexion");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Email MFA ──────────────────────────────────────────────────────────────
  const enableEmailMfa = async () => {
    clearMessages(); setActionLoading(true);
    try {
      const res = await fetch(`${config.baseURL}api/user/mfa/enable-email`, { method: "POST", headers: authHeader });
      if (res.ok) { setSuccess("MFA par email activé !"); fetchStatus(); }
      else        { setError("Erreur lors de l'activation"); }
    } catch {
      setError("Erreur de connexion");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Disable MFA ────────────────────────────────────────────────────────────
  const requestDisableChallenge = async () => {
    clearMessages(); setActionLoading(true);
    try {
      if (status?.mfaMethod === "EMAIL") {
        const res = await fetch(`${config.baseURL}api/user/mfa/disable-challenge`, { method: "POST", headers: authHeader });
        const data = await res.json();
        setDisableSessionToken(data.sessionToken);
        setSuccess("Un code vous a été envoyé par email pour confirmer la désactivation.");
      }
      setShowDisable(true);
    } catch {
      setError("Erreur lors de l'envoi du code");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDisable = async () => {
    if (!disableCode.trim()) { setError("Veuillez saisir le code"); return; }
    clearMessages(); setActionLoading(true);
    try {
      const res = await fetch(`${config.baseURL}api/user/mfa/disable`, {
        method:  "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body:    JSON.stringify({ code: disableCode, sessionToken: disableSessionToken }),
      });
      if (res.ok) {
        setSuccess("MFA désactivé avec succès.");
        setShowDisable(false);
        setDisableCode("");
        setRecoveryCodes([]);
        fetchStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.message || "Code incorrect");
      }
    } catch {
      setError("Erreur de connexion");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#1B738A" }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "20px",
        padding: "32px",
        backgroundColor: "white",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      }}>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SecurityIcon sx={{ color: "#1B738A", fontSize: 28 }} />
          <Typography variant="h5" sx={{ color: "#1B738A", fontWeight: "bold" }}>
            Sécurité &amp; Authentification à deux facteurs
          </Typography>
        </Box>

        {/* Info banner — explain what MFA is */}
        <Box sx={{ backgroundColor: "#EBF5FB", borderRadius: "10px", p: 2, mb: 3 }}>
          <Typography variant="body2" sx={{ color: "#1B4F72" }}>
            <strong>Qu'est-ce que le MFA ?</strong><br />
            Le MFA (authentification à deux facteurs) ajoute une couche de sécurité
            supplémentaire à votre compte. Après avoir saisi votre email et mot de passe,
            il vous sera demandé un code temporaire via votre application d'authentification
            ou par email.
          </Typography>
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={clearMessages}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>{success}</Alert>}

        {/* Status row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              Statut MFA
            </Typography>
            <Typography variant="body2" sx={{ color: "gray" }}>
              {status?.mfaEnabled
                ? `Activé — méthode : ${status.mfaMethod === "TOTP" ? "Application d'authentification" : "Email"}`
                : "Désactivé"}
            </Typography>
          </Box>
          <Switch
            checked={!!status?.mfaEnabled}
            disabled
            sx={{ "& .MuiSwitch-thumb": { backgroundColor: status?.mfaEnabled ? "#1B738A" : undefined } }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── MFA not enabled — setup options ── */}
        {!status?.mfaEnabled && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
              Choisissez votre méthode MFA :
            </Typography>

            {/* ── TOTP option ── */}
            {!setupData && (
              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "12px", p: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  📱 Application d'authentification (recommandé)
                </Typography>
                <Typography variant="caption" sx={{ color: "gray", display: "block", mb: 1.5 }}>
                  Fonctionne avec Google Authenticator, Microsoft Authenticator ou Portnet ID.
                  Génère un code à 6 chiffres toutes les 30 secondes. Ne nécessite pas d'internet.
                </Typography>
                <Button
                  variant="contained"
                  onClick={startTotpSetup}
                  disabled={actionLoading}
                  sx={{ backgroundColor: "#1B738A", "&:hover": { backgroundColor: "#155f73" }, borderRadius: "10px" }}
                >
                  {actionLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Configurer l'application"}
                </Button>
              </Box>
            )}

            {/* ── QR code enrollment flow ── */}
            {setupData && (
              <Box sx={{ border: "1px solid #1B738A", borderRadius: "12px", p: 2.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", textAlign: "center" }}>
                  Étape 1 — Scannez ce QR code
                </Typography>
                <Typography variant="caption" sx={{ color: "gray", textAlign: "center" }}>
                  Ouvrez votre application d'authentification et scannez ce code.
                </Typography>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupData.otpAuthUri)}`}
                  alt="QR Code MFA"
                  style={{ borderRadius: 8, border: "1px solid #e0e0e0" }}
                />
                <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: "8px", p: 1.5, width: "100%", textAlign: "center" }}>
                  <Typography variant="caption" sx={{ color: "gray" }}>
                    Ou entrez la clé manuellement :
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", fontSize: "15px", fontWeight: "bold", letterSpacing: "0.15em", mt: 0.5 }}>
                    {setupData.secretKey}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: "bold", alignSelf: "flex-start" }}>
                  Étape 2 — Confirmez avec le premier code
                </Typography>
                <TextField
                  fullWidth
                  label="Code à 6 chiffres de votre application"
                  value={setupCode}
                  onChange={(e) => { setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && confirmTotpSetup()}
                  inputProps={{ maxLength: 6, style: { letterSpacing: "0.4em", fontSize: "22px", textAlign: "center" } }}
                  autoFocus
                />
                <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                  <Button
                    fullWidth variant="contained" onClick={confirmTotpSetup} disabled={actionLoading}
                    sx={{ backgroundColor: "#1B738A", "&:hover": { backgroundColor: "#155f73" }, borderRadius: "10px" }}
                  >
                    {actionLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Confirmer et activer"}
                  </Button>
                  <Button variant="outlined" onClick={() => { setSetupData(null); setSetupCode(""); }} sx={{ borderRadius: "10px" }}>
                    Annuler
                  </Button>
                </Box>
              </Box>
            )}

            {/* ── Email OTP option ── */}
            {!setupData && (
              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "12px", p: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  📧 Code par email
                </Typography>
                <Typography variant="caption" sx={{ color: "gray", display: "block", mb: 1.5 }}>
                  Un code à 6 chiffres vous sera envoyé par email à chaque connexion.
                  Plus simple, mais dépend de la réception de votre email.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={enableEmailMfa}
                  disabled={actionLoading}
                  sx={{ borderColor: "#1B738A", color: "#1B738A", borderRadius: "10px" }}
                >
                  {actionLoading ? <CircularProgress size={20} /> : "Activer par email"}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* ── Recovery codes shown once after TOTP enrollment ── */}
        {recoveryCodes.length > 0 && (
          <Box sx={{ mt: 3, p: 2.5, backgroundColor: "#FFFDE7", borderRadius: "12px", border: "1px solid #F9A825" }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#E65100" }}>
              ⚠️ Codes de récupération — à sauvegarder maintenant
            </Typography>
            <Typography variant="caption" sx={{ color: "#555", display: "block", mb: 1.5 }}>
              Si vous perdez l'accès à votre application, utilisez l'un de ces codes à la place.
              Chaque code ne fonctionne <strong>qu'une seule fois</strong>. Conservez-les en lieu sûr.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.5 }}>
              {recoveryCodes.map((c, i) => (
                <Typography key={i} sx={{
                  fontFamily: "monospace", fontSize: "14px", fontWeight: "bold",
                  background: "white", p: "6px 10px", borderRadius: "6px",
                  border: "1px solid #ddd", textAlign: "center", letterSpacing: "0.1em",
                }}>
                  {c}
                </Typography>
              ))}
            </Box>
            <Button
              size="small" variant="outlined"
              sx={{ borderColor: "#E65100", color: "#E65100", borderRadius: "8px" }}
              onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); setSuccess("Codes copiés dans le presse-papiers !"); }}
            >
              Copier tous les codes
            </Button>
          </Box>
        )}

        {/* ── MFA enabled — disable option ── */}
        {status?.mfaEnabled && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: "gray", mb: 2 }}>
              Votre compte est protégé par le MFA via{" "}
              <strong>{status.mfaMethod === "TOTP" ? "application d'authentification" : "email"}</strong>.
            </Typography>

            {!showDisable ? (
              <Button
                variant="outlined" color="error"
                onClick={requestDisableChallenge}
                disabled={actionLoading}
                sx={{ borderRadius: "10px" }}
              >
                {actionLoading ? <CircularProgress size={20} /> : "Désactiver le MFA"}
              </Button>
            ) : (
              <Collapse in={showDisable}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, border: "1px solid #ffcdd2", borderRadius: "12px", p: 2, backgroundColor: "#fff8f8" }}>
                  <Typography variant="body2" sx={{ color: "#c62828", fontWeight: 500 }}>
                    Pour confirmer la désactivation, saisissez :
                  </Typography>
                  <Typography variant="caption" sx={{ color: "gray" }}>
                    {status.mfaMethod === "TOTP"
                      ? "Le code actuel de votre application d'authentification."
                      : "Le code reçu par email (vérifiez votre boîte de réception)."}
                  </Typography>
                  <TextField
                    fullWidth label="Code de confirmation" value={disableCode}
                    onChange={(e) => { setDisableCode(e.target.value); setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && confirmDisable()}
                    inputProps={{ maxLength: 9, style: { letterSpacing: "0.2em", textAlign: "center" } }}
                    autoFocus
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained" color="error"
                      onClick={confirmDisable} disabled={actionLoading}
                      sx={{ borderRadius: "10px", flex: 1 }}
                    >
                      {actionLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Confirmer la désactivation"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => { setShowDisable(false); setDisableCode(""); clearMessages(); }}
                      sx={{ borderRadius: "10px" }}
                    >
                      Annuler
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            )}
          </Box>
        )}

        {/* Back button */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="text"
            onClick={() => navigate(-1)}
            sx={{ color: "gray", textTransform: "none" }}
          >
            ← Retour
          </Button>
        </Box>

      </Box>
    </Container>
  );
};

export default MfaSettingsPage;
